import * as React from 'react';

import cx from 'classnames';

import { ImageAddMethod } from '~analytics/adapters/ImageEventAdapterTypes';
import { UnsupportedImageFormatEventAdapter } from '~analytics/adapters/UnsupportedImageFormatEventAdapter';

import { getImageDimensions } from '../../../api/images';

export type OnAddFilesHandler = ({
  files,
  addMethod,
}: {
  files: Array<File>;
  addMethod: ImageAddMethod;
}) => void;

interface Props {
  className?: string;
  id?: string;
  ariaLabel: string;
  multiple?: boolean;
  tabIndex?: number;
  disabled?: boolean;
  accept?: string[];
  onAddFiles: OnAddFilesHandler;
  isZoneActive: boolean;
  addImageIntercept?: () => void;
  children: React.ReactNode;
}

export const localZoneContext = React.createContext(false);

interface FileValidationParams {
  /** The file to be validated. */
  file: File;
  /** An array of accepted MIME types or file extensions. */
  acceptedTypes: string[];
}

/**
 * Validates the file type against an array of accepted MIME types and file extensions.
 */
function validateFileType({ file, acceptedTypes }: FileValidationParams) {
  const { name, type: mimeType = '' } = file;

  if (acceptedTypes.includes(mimeType)) return true;

  /**
   * If the accepted mime type includes a wildcard, just check if the
   * first part of the type matches. e.g. `image/*` → `image/png`
   */
  const doesMatchMimeType = acceptedTypes.some((valid) => {
    const [apexType, wildCard] = valid.split('/');
    const [testType] = mimeType.split('/');
    return wildCard === '*' && apexType === testType;
  });

  /**
   * Validate the file extension against the accepted types.
   */
  const fileExtensionRegExp = new RegExp(/\.[0-9a-z]+$/, 'i');
  const hasFileExtension = fileExtensionRegExp.test(name);
  if (!hasFileExtension) return false;
  const fileExtension = name.match(fileExtensionRegExp)[0];
  const validExtensions = acceptedTypes
    .filter((type) => !type.includes('*'))
    .map((type) => `.${type.split('/')[1]}`);
  const doesMatchExtension = validExtensions.includes(fileExtension);

  return doesMatchMimeType || doesMatchExtension;
}

export function ImageFieldDropZone({
  className,
  id,
  ariaLabel,
  multiple = false,
  tabIndex = 0,
  disabled = false,
  accept = ['image/*'],
  onAddFiles = () => {},
  isZoneActive,
  addImageIntercept,
  children,
}: Props) {
  const inputRef = React.createRef<HTMLInputElement>();
  const [localZoneActive, setLocalZoneActive] = React.useState(false);

  const { Provider: LocalZoneProvider } = localZoneContext;

  const containerClass = cx('image-field__dropzone', {
    'is-active': isZoneActive || localZoneActive,
    [className]: className,
  });

  const labelClass = cx(
    'relative flex items-center justify-center w-full h-full text-grey-600 hover:text-cyan-600 focus:text-cyan-600 rounded-1 outline-none',
    'image-field__label',
    {
      'cursor-pointer': !disabled,
      'text-cyan-600': localZoneActive,
    }
  );
  const zoneBorderClass = cx(
    'absolute z-0 border-dashed border-2 border-cyan-600 rounded-1 -inset-2',
    {
      'opacity-0': !isZoneActive,
    }
  );

  return (
    <LocalZoneProvider value={localZoneActive}>
      <div
        className={containerClass}
        data-drop-zone
        data-testid="image-field-dropzone"
        onDragOver={(event) => {
          event.stopPropagation();
          event.preventDefault();

          if (disabled) return;

          setLocalZoneActive(true);
        }}
        onDragLeave={(event) => {
          event.stopPropagation();
          event.preventDefault();

          if (disabled) return;
        }}
        onDrop={(event) => {
          event.stopPropagation();
          event.preventDefault();

          if (disabled) return;

          setLocalZoneActive(false);

          const files = [].slice.call(event.dataTransfer.files);
          const file = files[0];

          if (!validateFileType({ file, acceptedTypes: accept })) {
            getImageDimensions(file).then(({ height, width }) => {
              new UnsupportedImageFormatEventAdapter({
                add_method: ImageAddMethod.DragDrop,
                height,
                width,
                file_size: file.size,
                file_type: file.file_type,
              });
            });

            return;
          }

          onAddFiles({ files: [file], addMethod: ImageAddMethod.DragDrop });
        }}
      >
        <label
          className={labelClass}
          tabIndex={tabIndex || 0}
          onFocus={() => {
            setLocalZoneActive(true);
          }}
          onBlur={() => {
            setLocalZoneActive(false);
          }}
          onKeyUp={(event: React.KeyboardEvent<HTMLLabelElement>) => {
            inputRef.current.value = '';
            switch (event.key) {
              case ' ':
              case 'Enter':
                inputRef.current.click();
                break;
            }
          }}
          onClick={(event: React.MouseEvent<HTMLLabelElement>) => {
            inputRef.current.value = '';
            if (addImageIntercept) {
              addImageIntercept();
              event.stopPropagation();
              event.preventDefault();
              return;
            }
          }}
        >
          <div className={zoneBorderClass} />
          {children}
          <input
            type="file"
            id={id}
            className="sr-only"
            aria-label={ariaLabel}
            ref={inputRef}
            tabIndex={-1}
            disabled={disabled}
            accept={accept.join(',')}
            //name={name}
            multiple={multiple}
            onChange={(event) => {
              event.preventDefault();
              const files = [].slice.call(event.target.files);

              const file = files[0];
              if (!validateFileType({ file, acceptedTypes: accept })) {
                getImageDimensions(file).then(({ height, width }) => {
                  new UnsupportedImageFormatEventAdapter({
                    add_method: ImageAddMethod.DragDrop,
                    height,
                    width,
                    file_size: file.size,
                    file_type: file.file_type,
                  });
                });

                return;
              }
              onAddFiles({ files: [file], addMethod: ImageAddMethod.Button });
            }}
          />
        </label>
      </div>
    </LocalZoneProvider>
  );
}
